import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, Menu, Space } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { SorterResult, TableRowSelection } from 'antd/lib/table/interface';
import { tableSorterOrder } from '@/utils/table';
import { useListRequestPurchases } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.hooks';
import {
  RequestPurchaseColumnConfig,
  RequestPurchaseFieldOption,
  RequestPurchases,
  RequestPurchasesQuery,
  RequestPurchaseType,
} from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';

import { quantityFormatter } from '@/utils/number';
import { FcInfo } from 'react-icons/fc';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { AppPageHeader, InfiniteTable } from '@/components/_common';
import { useDebounce } from '@/hooks';
import QuotationPartsExtraFilters from '../QuotationPartsExtraFilters';

interface Props {
  rowSelection?: TableRowSelection<RequestPurchases>;
  enabled?: boolean;
  companyId?: number;
}

const RequestPurchasesList: React.FC<Props> = ({
  enabled = true,
  rowSelection,
  companyId,
}: Props) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<RequestPurchasesQuery>({
    order: 'DESC',
    type: RequestPurchaseType.part,
    companyIdFilter: companyId,
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useListRequestPurchases(queryDebounce, enabled);

  const requestPurchases = flatInfiniteData(data);

  function handleOrderRequestPurchase(sorter: SorterResult<RequestPurchases>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (
            prev.orderByPurchaseQuotation ===
            RequestPurchaseColumnConfig[sorter.columnKey]
          ) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderByPurchaseQuotation:
              RequestPurchaseColumnConfig[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  const columns: ColumnsType<RequestPurchases> = [
    {
      ellipsis: true,
      width: 140,
      dataIndex: 'workOrderNumber',
      key: RequestPurchaseFieldOption.workOrder,
      title: intl.formatMessage({
        id: 'commons.table.column.workOrder',
      }),
      sorter: true,
      render: (value) => value ?? '-',
    },
    {
      dataIndex: 'licensePlate',
      key: RequestPurchaseFieldOption.licensePlate,
      ellipsis: true,
      width: 140,
      title: intl.formatMessage({
        id: 'commons.table.column.licensePlate',
      }),
      sorter: true,
      render: (value) => value ?? '-',
    },
    useConfigurationColumnsPart({
      internalCode: ['item', 'sku'],
      manufacturingCode: ['item', 'manufacturingCode'],
      barcode: ['item', 'barCode'],
      companyCode: ['item', 'companyCode'],
    }),
    {
      ellipsis: true,
      dataIndex: 'description',
      key: RequestPurchaseFieldOption.description,
      title: intl.formatMessage({
        id: 'commons.table.column.description',
      }),
      width: 300,
      sorter: true,
    },
    {
      width: 40,
      render: (_value, row) => {
        return (
          <Dropdown
            overlay={
              <Menu mode="vertical" style={{ width: 250 }}>
                <Menu.Item key={1}>{`Total Solicitado: ${quantityFormatter(
                  row.requestedQuantity,
                )}`}</Menu.Item>
                <Menu.Item key={2}>{`Total em cotação: ${quantityFormatter(
                  row.quantityQuotation,
                )}`}</Menu.Item>
                <Menu.Item key={3}>{`Enviado para Compra: ${quantityFormatter(
                  row.quantitySentPurchased,
                )}`}</Menu.Item>
                <Menu.Item key={4}>{`Total comprado: ${quantityFormatter(
                  row.quantityPurchased,
                )}`}</Menu.Item>
                <Menu.Item key={5}>{`Total a comprar: ${quantityFormatter(
                  row.total,
                )}`}</Menu.Item>
              </Menu>
            }
            trigger={['click']}
            arrow
            placement="topCenter"
          >
            <FcInfo size={20} id="note-type-icon" cursor={'pointer'} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <AppPageHeader
        onSearch={(value: string) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        extraFilters={
          <QuotationPartsExtraFilters
            isPartOs
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable
        rowSelection={rowSelection}
        columns={columns}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrderRequestPurchase(sorter);
          }
        }}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        dataSource={requestPurchases}
        loading={isFetching}
        className="scroll-table"
        rowKey={(row) => row.id}
      />
    </Space>
  );
};

export default RequestPurchasesList;
